<template>
    <div class="user-form">
        <div v-if="dialogMode === 'add'" class="form-field">
            <label for="email">Email*</label>
            <InputText
                id="email"
                v-model.trim="formData.email"
                class="w-full"
                :class="{ 'p-invalid': !!emailError }"
                aria-describedby="email-error"
            />
            <small id="email-error" class="p-error" v-if="emailError">
                {{ emailError }}
            </small>
        </div>
        <div v-if="dialogMode === 'add'" class="form-field">
            <label for="name">Name</label>
            <InputText
                id="name"
                v-model.trim="formData.name"
                class="w-full"
                :class="{ 'p-invalid': submitted }"
                aria-describedby="name-error"
            />
        </div>
        <div class="form-field">
            <label for="roles">Roles</label>
            <MultiSelect
                id="roles"
                v-model="formData.roles"
                :options="roles"
                optionLabel="label"
                optionValue="value"
                placeholder="Select roles"
                :showToggleAll="false"
                class="w-full"
                :class="{ 'p-invalid': !!roleError }"
                aria-describedby="role-error"
            />
            <small id="role-error" class="p-error" v-if="roleError">
                {{ roleError }}
            </small>
        </div>
        <div class="buttons">
            <Button
                label="Cancel"
                severity="secondary"
                @click="dialogRef?.close"
            />
            <Button
                :label="dialogMode === 'add' ? 'Add User' : 'Update Role'"
                severity="primary"
                @click="submit"
                :loading="isSubmitting"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import type { DynamicDialogInstance } from "primevue/dynamicdialogoptions";
import MultiSelect from "primevue/multiselect";
import { useToast } from "primevue/usetoast";
import { Ref, computed, inject, onMounted, ref } from "vue";

import type {
    UserDialogData,
    UserFormData,
} from "@/composables/user-management-dialog";
import { ClusterRole } from "@/stores/user-store";
import { useUsersStore } from "@/stores/users-store";

const dialogRef = inject<Ref<DynamicDialogInstance> | undefined>("dialogRef");

// Access dialog props and methods
const toast = useToast();
const usersStore = useUsersStore();

// Get data passed to the dialog
const dialogData = dialogRef!.value.data as UserDialogData;
const dialogMode = ref<"add" | "edit">(dialogData.mode);

// Form state
const submitted = ref(false);
const isSubmitting = ref(false);
const formData = ref<UserFormData>({
    email: "",
    roles: [],
    userId: "",
});

// Email validation
const isValidEmail = computed(() => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return !formData.value.email || emailRegex.test(formData.value.email);
});

// Validation error messages
const emailError = computed(() => {
    if (!submitted.value) return "";
    if (!formData.value.email) return "Email address is required";
    if (!isValidEmail.value)
        return "Please enter a valid email address (e.g., user@company.com)";
    return "";
});

// Empty roles means view-only (VIEWER is sent implicitly on submit), so no validation needed.
const roleError = computed(() => "");

// VIEWER is the implicit base role and is not shown as a selectable option.
// An empty selection means view-only; VIEWER is sent to the backend on submit.
// ORGANIZATION_MANAGER can only be assigned via management commands, not through the UI.
const roles = [
    { label: "Editor", value: ClusterRole.USER },
    { label: "Stek Manager", value: ClusterRole.STEK_MANAGER },
    { label: "User Manager", value: ClusterRole.USER_MANAGER },
];

// Initialize the form
onMounted(() => {
    if (dialogMode.value === "edit" && dialogData.userData) {
        formData.value = {
            name: dialogData.userData.name,
            email: dialogData.userData.email,
            // Filter out VIEWER (implicit) and ORGANIZATION_MANAGER (not assignable via UI).
            roles: (dialogData.userData.roles as ClusterRole[]).filter(
                (r) =>
                    r !== ClusterRole.VIEWER &&
                    r !== ClusterRole.ORGANIZATION_MANAGER
            ),
            userId: dialogData.userData.identity,
        };
    }
});

const submit = async () => {
    submitted.value = true;

    // Validate form
    if (emailError.value || roleError.value) {
        toast.add({
            severity: "warn",
            summary: "Please fix the validation errors",
            detail: "Check the form fields and correct any errors before submitting.",
            life: 4000,
        });
        return;
    }

    isSubmitting.value = true;
    try {
        // Empty selection means view-only; send VIEWER explicitly to the backend.
        const rolesToSend =
            formData.value.roles.length > 0
                ? formData.value.roles
                : [ClusterRole.VIEWER];

        if (dialogMode.value === "add") {
            await usersStore.addUserToOrganization(
                formData.value.email,
                rolesToSend,
                formData.value.name
            );
        } else {
            await usersStore.updateUserRoles(
                formData.value.userId,
                rolesToSend
            );
        }
        dialogRef?.value.close();
    } catch (error) {
        // The error is already handled by the store's handleError function
        // which shows appropriate toast messages, so we don't need to show another one here
        console.error("Error in user management dialog:", error);
    } finally {
        isSubmitting.value = false;
    }
};
</script>

<style scoped lang="scss">
.user-form {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-bottom: 16px;
}

.form-field {
    display: flex;
    flex-direction: column;
    gap: 6px;

    label {
        font-weight: 500;
    }
}

.w-full {
    width: 100%;
}

.buttons {
    display: flex;
    justify-content: end;
    margin-top: 1em;
    gap: 1em;
}
</style>
